import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { GET_CAMPAIGN_SITES, GET_USER_ACCOUNTS } from '../graphql';
import { GetUserAccountsResponse } from '../types/graphql';
import { useAuth } from './AuthContext';
import { showInkToast } from '../components/cms/InkToast';

export type UserAccount = GetUserAccountsResponse['userAccounts'][number];

// The open site is per tab: sessionStorage holds this tab's site (it survives a
// reload but not another tab's switch), and localStorage holds the last site
// picked in any tab, which only a new tab starts from. One shared key let a
// switch — or a lost selection — in one tab move every other tab on its next
// load (UX review round 8: a tab on TinyCMS came back on KT Newsroom).
const TAB_ACCOUNT_STORAGE_KEY = 'selected_account_id';
const LAST_ACCOUNT_STORAGE_KEY = 'selected_account_id';

function rememberAccount(id: string | null) {
  if (id) {
    sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, id);
    localStorage.setItem(LAST_ACCOUNT_STORAGE_KEY, id);
  } else {
    sessionStorage.removeItem(TAB_ACCOUNT_STORAGE_KEY);
    localStorage.removeItem(LAST_ACCOUNT_STORAGE_KEY);
  }
}

function pickDefaultAccount(accounts: UserAccount[]): {
  account: UserAccount;
  /** A remembered site that this login no longer has. */
  lost: boolean;
} {
  const rememberedId =
    sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY) ||
    localStorage.getItem(LAST_ACCOUNT_STORAGE_KEY);
  const remembered = rememberedId ? accounts.find((a) => a.id === rememberedId) : undefined;
  if (remembered) return { account: remembered, lost: false };

  // Stable fallback: oldest membership first (matches prior page init behavior).
  const oldest = [...accounts].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )[0];
  return { account: oldest, lost: Boolean(rememberedId) };
}

interface AccountContextType {
  accounts: UserAccount[];
  selectedAccount: UserAccount | null;
  setSelectedAccount: (account: UserAccount | null) => void;
  selectAccountById: (accountId: string) => void;
  accountId: string | undefined;
  isLoading: boolean;
  error?: ApolloError;
  refetchAccounts: () => Promise<unknown>;
}

const AccountContext = createContext<AccountContextType | null>(null);

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [selectedAccount, setSelectedAccountState] = useState<UserAccount | null>(null);

  const { data, loading, error, refetch } = useQuery<GetUserAccountsResponse>(GET_USER_ACCOUNTS, {
    skip: !isAuthenticated,
  });
  // Sites where this person holds only the Campaign role: listed with their
  // own sites, marked campaignRole, with none of the site's settings. A server
  // without it yet answers with an error, which means none.
  const {
    data: campData,
    loading: campLoading,
    refetch: refetchCampaignSites,
  } = useQuery<{
    campaignSites: Array<{
      accountId: string;
      name: string;
      createdAt: string | null;
      approverName: string;
      campaigns: number;
    }>;
  }>(GET_CAMPAIGN_SITES, { skip: !isAuthenticated, errorPolicy: 'all' });

  const accounts = useMemo<UserAccount[]>(
    () => [
      ...(data?.userAccounts ?? []),
      ...(campData?.campaignSites ?? []).map(
        (s): UserAccount => ({
          id: s.accountId,
          name: s.name,
          createdAt: s.createdAt || new Date(0).toISOString(),
          plan: '',
          isActive: true,
          techContactEmail: '',
          // Campaigns live in the Pro shell (Reach); nothing else opens.
          products: [
            { product: 'tinycms', status: 'active' },
            { product: 'atomizer', status: 'active' },
            { product: 'reach', status: 'active' },
          ],
          campaignRole: true,
          approverName: s.approverName,
        })
      ),
    ],
    [data?.userAccounts, campData?.campaignSites]
  );

  // Resolve / repair selection whenever memberships load or change. The site
  // only ever changes here when it has to — none chosen yet, or this login lost
  // the one that was open — and then a notice says so.
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedAccountState(null);
      return;
    }
    if (loading || campLoading) return;
    // A failed load is not "no sites": keep the open site and its saved id.
    // Dropping them here sent the next good load to the oldest site.
    if (error || !data) return;

    if (accounts.length === 0) {
      setSelectedAccountState(null);
      rememberAccount(null);
      return;
    }

    if (selectedAccount) {
      // Keep the selection object fresh (name/plan/products may have changed).
      const fresh = accounts.find((account) => account.id === selectedAccount.id);
      if (fresh) {
        if (fresh !== selectedAccount) setSelectedAccountState(fresh);
        return;
      }
    }

    const { account, lost } = pickDefaultAccount(accounts);
    setSelectedAccountState(account);
    sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, account.id);
    if (selectedAccount || lost) {
      showInkToast(
        `The site you had open isn’t available to this login any more. Now showing ${account.name}.`
      );
    }
  }, [accounts, data, error, isAuthenticated, loading, campLoading, selectedAccount]);

  const setSelectedAccount = useCallback((account: UserAccount | null) => {
    setSelectedAccountState(account);
    rememberAccount(account?.id ?? null);
  }, []);

  const selectAccountById = useCallback(
    (accountId: string) => {
      const account = accounts.find((item) => item.id === accountId) ?? null;
      setSelectedAccount(account);
    },
    [accounts, setSelectedAccount]
  );

  const refetchAccounts = useCallback(
    () => Promise.all([refetch(), refetchCampaignSites().catch(() => null)]),
    [refetch, refetchCampaignSites]
  );

  const value = useMemo(
    () => ({
      accounts,
      selectedAccount,
      setSelectedAccount,
      selectAccountById,
      accountId: selectedAccount?.id,
      isLoading: isAuthenticated && (loading || campLoading),
      error,
      refetchAccounts,
    }),
    [
      accounts,
      selectedAccount,
      setSelectedAccount,
      selectAccountById,
      isAuthenticated,
      loading,
      campLoading,
      error,
      refetchAccounts,
    ]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};
